import DoneIcon from '@mui/icons-material/Done';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container, Stack } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form8949 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 8949 Guide: Report Capital Asset Sales & Exchanges"
          description="Learn how to file Form 8949 for capital asset sales. Get insights on requirements, filing steps, and how TaxZerone simplifies your tax experience."
          keywords=""
          canonical="resources/tax-exempt/form-8949-sales-and-dispositions-guide/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form{' '}
          <span className="highlight-container">
            <span className="highlight">8949</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Sales and Other Dispositions of Capital Assets
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form8949`}
          >
            E-file Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-8949-sales-and-dispositions-guide/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 8949 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Form 8949
                        </strong>{' '}
                        is used to report{' '}
                        <strong style={{ fontWeight: 600 }}>
                          sales and exchanges of capital assets{' '}
                        </strong>
                        and reconcile transaction amounts reported on{' '}
                        <strong style={{ fontWeight: 600 }}>Form</strong>{' '}
                        <Link
                          href="https://testweb.variniral.com/information-returns/efile-irs-form-1099-b/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          {' '}
                          1099-B
                        </Link>
                        ,{' '}
                        <Link
                          href="https://testweb.variniral.com/information-returns/efile-irs-form-1099-da/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          {' '}
                          1099-DA
                        </Link>
                        ,{' '}
                        <Link
                          href="https://testweb.variniral.com/information-returns/efile-irs-form-1099-s/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          1099-S
                        </Link>{' '}
                        with amounts reported on a tax return.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Taxpayers and entities generally use{' '}
                        <strong style={{ fontWeight: 600 }}>Form 8949 </strong>
                        when reporting capital asset transactions that must be
                        separately reported under the applicable IRS rules.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Form 8949 reports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          short-term and long-term capital gains and
                          losses,{' '}
                        </strong>
                        including transactions involving{' '}
                        <strong style={{ fontWeight: 600 }}>
                          stocks, bonds, digital assets, real estate,
                        </strong>{' '}
                        and other capital assets.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Supported Tax Returns:
                        </strong>{' '}
                        Form 8949 may be filed with{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-T
                        </Link>
                        ,{' '}
                        <strong style={{ fontWeight: 600 }}>Form 1041,</strong>{' '}
                        <Link
                          href="https://www.taxzerone.com/business-tax/efile-irs-form-1065-online/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          1065
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/business-tax/efile-irs-form-1120-s/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 1120-S
                        </Link>
                        ,{' '}
                        <strong style={{ fontWeight: 600 }}> Form 1120</strong>{' '}
                        and{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-5227/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 5227
                        </Link>{' '}
                        , depending on the applicable capital asset transactions
                        and IRS reporting requirements.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What’s New in Form 8949?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Understanding the latest updates can help you report your
                  transactions accurately. Here’s what you need to know:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  How should you report digital asset transactions?
                </Typography>

                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer
                    sx={{
                      border: '1px solid #828282',
                      borderCollapse: 'collapse',
                      '& td': { py: 0.5 },
                    }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor: '#ccc',
                          }}
                        >
                          <TableCell
                            sx={{
                              color: '#333',
                              width: 300,
                              borderRight: '1px solid #828282',
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                              fontSize: 16,
                              fontWeight: 700,
                            }}
                          >
                            Transaction Type
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#333',
                              width: 300,
                              borderRight: '1px solid #828282',
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                              fontSize: 16,
                              fontWeight: 700,
                            }}
                          >
                            Boxes to Use
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#333',
                              width: 300,
                              borderRight: '1px solid #828282',
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                              fontSize: 16,
                              fontWeight: 700,
                            }}
                          >
                            Form Section
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#333',
                              width: 300,
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                              fontSize: 16,
                              fontWeight: 700,
                            }}
                          >
                            Do Not Use
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Short-term digital asset transactions
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Boxes G, H, or I
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Part I of Form 8949
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Box C
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                            }}
                          >
                            Long-term digital asset transactions
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                            }}
                          >
                            Boxes J, K, or L
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',

                              textAlign: 'left',
                            }}
                          >
                            Part II of Form 8949
                          </TableCell>
                          <TableCell
                            sx={{
                              // borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Box F
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 4, fontStyle: 'italic' }}>
                  We explain the purpose and usage of each box in detail in the
                  section below to help you select the correct option.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is IRS Form 8949?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file IRS Form 8949?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#attach" sx={{ color: '#0202ff' }}>
                          Why attach Form 8949 to Form 990-T?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why attach Form 8949 to Form 5227?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to file Form 8949 with TaxZerone?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#add" sx={{ color: '#0202ff' }}>
                          Additional IRS References
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whatis"
                >
                  What is IRS Form 8949?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> IRS tax Form 8949</span> is
                  used to report detailed sales and exchanges of capital assets.
                  Instead of listing only totals, the IRS requires each
                  transaction to be disclosed separately unless it qualifies for
                  summary reporting.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Capital Assets Reported on Form 8949
                </Typography>
                <Typography variant="body1">
                  Capital assets generally include property held for investment
                  or certain business-related purposes. Examples of such assets
                  include:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.3 } }}>
                  <li>Stocks and bonds </li>
                  <li>Real estate </li>
                  <li>Cryptocurrency and digital assets </li>
                  <li>Partnership interests </li>
                  <li>Other investment assets</li>{' '}
                </Box>
                <Typography variant="body1">
                  In addition to standard asset sales, Form 8949 is utilized to
                  report transactions that require specific or special tax
                  treatment, including the following:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.3 } }}>
                  <li>
                    Capital losses, nondeductible losses, and losses from wash
                    sales.
                  </li>
                  <li>Short sales </li>
                  <li>Traders in securities reporting </li>
                  <li>Gain or loss from options </li>
                  <li>Installment sales </li>
                  <li>Demutualization of life insurance companies </li>
                  <li>
                    Exclusion or rollover of gain from qualified small business
                    (QSB) stock{' '}
                  </li>
                  <li>Other rollover transactions </li>
                  <li>
                    Deferral of gain invested in a Qualified Opportunity Fund
                    (QOF){' '}
                  </li>
                  <li>
                    Special reporting situations for corporations, partnerships,
                    estates, and trusts{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  These special situations do not change what a capital asset is
                  - but they affect{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    how the transaction is reported
                  </span>{' '}
                  on IRS Form 8949.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who must file IRS Form 8949?
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  You must file{' '}
                  <span style={{ fontWeight: 600 }}> Form 8949 IRS</span> if:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.3 } }}>
                  <li>You sold or exchanged a capital asset</li>
                  <li>You received Form 1099-B </li>
                  <li>You need to adjust cost basis </li>
                  <li>Your brokerage did not report basis to the IRS </li>
                  <li>Schedule D requires transaction detail </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  This applies to:{' '}
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.3 } }}>
                  <li>Individuals</li>
                  <li>Corporations </li>
                  <li>Estates </li>
                  <li>Trusts </li>
                  <li>Tax-exempt organizations with taxable activity </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Even exempt entities may need{' '}
                  <span style={{ fontWeight: 600 }}>
                    IRS Form 8949 and Schedule D
                  </span>{' '}
                  when reporting taxable capital gains.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Choose TaxZerone - Your Gateway to Seamless Tax Filing
                      </Typography>
                      <Typography variant="body2">
                        Complete your Form 990-T return and 8949 filing
                        requirements with ease!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 120, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form8949`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }} >
                 <strong style={{fontWeight:600}} >Note:</strong> The deadline is extended to the next business day
                  if the due date falls on a weekend or a legal holiday.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="attach"
                >
                  Why attach Form 8949 to Form 990-T?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Organizations filing IRS Form 990-T report Unrelated Business
                  Taxable Income (UBTI). When capital gains are connected to
                  unrelated trade or business activity, IRS tax Form 8949 may be
                  required.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Common Situations
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.3 } }}>
                  <li>Sale of property used in unrelated business </li>
                  <li>Sale of partnership interest generating UBTI </li>
                  <li>Disposition of unrelated business assets </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    form 8949 instructions 2025
                  </span>{' '}
                  clarify that detailed reporting must occur before completing
                  certain Schedule D totals.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="why"
                >
                  Why attach Form 8949 to Form 5227?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Split-interest trusts filing IRS Form 5227 frequently report
                  investment activity. Because these trusts often sell
                  securities during the year, Form 8949 IRS reporting ensures
                  each transaction is properly documented.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Why it matters
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.3 } }}>
                  <li>Tracks short-term vs long-term gains </li>
                  <li>Supports Schedule D calculations </li>
                  <li>Ensures proper income tier classification </li>
                  <li>Provides transparency for trust reporting</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For many trusts,{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    IRS Form 8949 and Schedule D
                  </span>{' '}
                  are used together annually.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="howto"
                >
                  How to file Form 8949 with TaxZerone?
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '20px !important',
                    fontWeight: 600,
                    mb: 0.8,
                    lineHeight: 1.5,
                    scrollMarginTop: 89,
                  }}
                  id="part1"
                >
                  Part I - Short-Term
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Short-Term Capital Gains and Losses{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.3 } }}>
                  <li>
                    A{' '}
                    <span style={{ fontWeight: 600 }}>
                      short-term capital gain or loss
                    </span>{' '}
                    occurs when you sell or dispose of a capital asset that you{' '}
                    <span style={{ fontWeight: 600 }}>
                      held for one year or less.
                    </span>{' '}
                    The holding period begins{' '}
                    <span style={{ fontWeight: 600 }}>
                      the day after you acquire the property
                    </span>{' '}
                    and includes{' '}
                    <span style={{ fontWeight: 600 }}>
                      the day you sell or dispose of it.
                    </span>
                  </li>
                  <li>
                    Short-term transactions must generally be reported in{' '}
                    <span style={{ fontWeight: 600 }}>
                      Part I of Form 8949.
                    </span>{' '}
                    Each sale or exchange of a capital asset-such as stocks,
                    bonds, or other investments- is listed here with details
                    like the purchase date, sale date, proceeds, and cost basis.
                  </li>
                  <li>
                    In some cases, you may not need to list each transaction on
                    Form 8949. If the transaction meets certain IRS exceptions,
                    it can be reported directly on{' '}
                    <span style={{ fontWeight: 600 }}>
                      line 1a of Schedule D
                    </span>
                    instead.
                  </li>
                  <li>
                    There are also special situations to keep in mind. For
                    example, a{' '}
                    <span style={{ fontWeight: 600 }}>
                      non-business bad debt
                    </span>{' '}
                    is treated as a{' '}
                    <span style={{ fontWeight: 600 }}>
                      short-term capital loss,
                    </span>{' '}
                    even if the debt existed for more than one year.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f8949-part1-img.webp" />
                  <source srcSet="/assets/images/f8949-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8949-part1-img.png"
                    loading="lazy"
                    alt=" Form 8949 showing Part I short-term transaction boxes A-I and Schedule D filing instructions."
                    width={760}
                    height={402}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 22, sm: 20, xs: 18 },
                    mb: 0.8,
                  }}
                >
                  Selecting the Correct Reporting Box for Short-Term
                  Transactions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Before entering transaction details in{' '}
                  <span style={{ fontWeight: 600 }}>Part I of Form 8949,</span>{' '}
                  you must{' '}
                  <span style={{ fontWeight: 600 }}>
                    select the correct reporting category
                  </span>{' '}
                  by checking <span style={{ fontWeight: 600 }}>one box</span>{' '}
                  at the top of the form. These boxes help the IRS understand
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    how your transaction was reported to you and whether the
                    cost basis information was also reported to the IRS.
                  </span>
                </Typography>
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#c026521f',
                    borderLeft: '5px solid #c02652',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                    Important:
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      listStyleType: 'none',
                      paddingLeft: 0,
                      textAlign: 'left',
                      ml: { xs: 0, sm: 3 },
                      mt: { xs: 0, md: 3 },
                      mb: { xs: 0, md: 3 },
                      '& li': { display: 'flex' },
                    }}
                  >
                    <li style={{ marginBottom: 8 }}>
                      <DoneIcon
                        sx={{ ml: -1, mb: -0.5, mr: 0.5, color: '#000000' }}
                      />
                      Check only one box that best describes your short-term
                      transactions.{' '}
                    </li>
                    <li style={{ marginBottom: 8 }}>
                      <DoneIcon
                        sx={{ ml: -1, mb: -0.5, mr: 0.5, color: '#000000' }}
                      />
                      If your transactions fall under more than one category,
                      you must complete a separate Form 8949 for each applicable
                      box.
                    </li>
                    <li>
                      <DoneIcon
                        sx={{ ml: -1, mb: -0.5, mr: 0.5, color: '#000000' }}
                      />
                      If you have more transactions than can fit on one page,
                      you may attach additional copies of Form 8949 with the
                      same box checked.
                    </li>
                  </Box>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 22, sm: 20, xs: 18 },
                    mb: 0.8,
                  }}
                >
                  Box Options for Short-Term Transactions
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Box A - Basis Reported to the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check <span style={{ fontWeight: 600 }}>Box A</span> if your
                  short-term transactions were reported to you on{' '}
                  <span style={{ fontWeight: 600 }}>Form 1099-B</span>, and the
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    cost basis was also reported to the IRS
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Box B - Basis Not Reported to the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check <span style={{ fontWeight: 600 }}>Box B</span> if you
                  received <span style={{ fontWeight: 600 }}>Form 1099-B</span>,
                  but the{' '}
                  <span style={{ fontWeight: 600 }}>
                    cost basis was not reported to the IRS
                  </span>
                  . In this case, you must provide the cost basis when reporting
                  the transaction.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Box C - Transactions Not Reported on Form 1099
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check <span style={{ fontWeight: 600 }}>Box C</span> if the
                  short-term transaction{' '}
                  <span style={{ fontWeight: 600 }}>
                    was not reported to you on Form 1099-B or a digital asset
                    form.
                  </span>{' '}
                  You still need to report the transaction using your own
                  records.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Box G - Digital Asset Transactions with Basis Reported
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check <span style={{ fontWeight: 600 }}>Box G</span> if your{' '}
                  <span style={{ fontWeight: 600 }}>
                    digital asset transactions
                  </span>{' '}
                  were reported on{' '}
                  <span style={{ fontWeight: 600 }}>Form 1099-DA</span>, and the{' '}
                  <span style={{ fontWeight: 600 }}>
                    cost basis was reported to the IRS.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Box H - Digital Asset Transactions with Basis Not Reported
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check <span style={{ fontWeight: 600 }}>Box H</span> if your
                  digital asset transactions were reported on{' '}
                  <span style={{ fontWeight: 600 }}>Form 1099-DA</span>, but the{' '}
                  <span style={{ fontWeight: 600 }}>
                    cost basis was not reported to the IRS.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Box I - Digital Asset Transactions Not Reported
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check <span style={{ fontWeight: 600 }}>Box I</span> if you
                  had short-term digital asset transactions that were{' '}
                  <span style={{ fontWeight: 600 }}>
                    not reported to you on Form 1099-DA or Form 1099-B
                  </span>
                  .
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8949-part-I-table-img.webp" />
                  <source srcSet="/assets/images/f8949-part-I-table-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8949-part-I-table-img.png"
                    loading="lazy"
                    alt="Form 8949 Part I table with columns for description of property, dates acquired and sold, proceeds, cost basis, adjustments, and gain or loss."
                    width={760}
                    height={529}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, xs: 18 },
                    mb: 0.8,
                  }}
                >
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a) – Description of Property{' '}
                </Typography>
                <Typography variant="body1">
                  Enter a clear description of what you sold. Include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Number of shares (for stocks)</li>
                  <li>Stock ticker symbol (optional but recommended)</li>
                  <li>Full name or symbol of digital asset</li>
                  <li>Exact quantity sold</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b) – Date Acquired
                </Typography>
                <Typography variant="body1">
                  Enter the date you originally purchased or acquired the asset.
                  Special cases:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If purchased on multiple dates Enter{' '}
                    <span style={{ fontWeight: 600 }}> “VARIOUS” </span>
                  </li>
                  <li>
                    If inherited Enter{' '}
                    <span style={{ fontWeight: 600 }}>“INHERITED”</span>{' '}
                  </li>
                  <li>
                    For exchange-traded assets Use the{' '}
                    <span style={{ fontWeight: 600 }}>trade date</span>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This date determines whether your transaction qualifies as
                  short-term or long-term.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c) – Date Sold or Disposed Of
                </Typography>
                <Typography variant="body1">
                  Enter the actual sale date. The IRS uses this date to confirm:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>The tax year of the sale </li>
                  <li>Holding period classification</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (d) – Proceeds
                </Typography>
                <Typography variant="body1">
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    gross amount received
                  </span>{' '}
                  from the sale.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Use the number shown on Form 1099-B or 1099-DA. </li>
                  <li>Do not subtract fees unless already adjusted. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (e) – Cost or Other Basis
                </Typography>
                <Typography variant="body1">
                  Enter what you originally paid for the asset, including:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Purchase price </li>
                  <li>Commissions</li>
                  <li>Transaction fees </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Accurate basis reporting prevents overpaying taxes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (f) – Codes from instructions
                </Typography>
                <Typography variant="body1">
                  Enter adjustment codes if needed. Common reasons:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Wash sale adjustments </li>
                  <li>Incorrect basis correction </li>
                  <li>Special exclusions </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If no adjustment applies, leave blank.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (g) – Adjustment Amount
                </Typography>
                <Typography variant="body1">
                  If you entered a code in Column (f), enter the adjustment
                  amount here.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Negative numbers go in parentheses. </li>
                  <li>This modifies your gain or loss calculation. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (h) – Gain or (Loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This column calculates your result:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Proceeds (d) – Cost Basis (e)  ± Adjustment (g) = Gain or Loss
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  If positive Gain
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If negative Loss{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These totals flow to Schedule D, where your final capital gain
                  tax is determined.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, xs: 18 },
                    mb: 0.8,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Line 2 of Form 8949 helps reconcile your total capital gains
                  or losses with what you report on Schedule D (Form 1040).
                  First, add all amounts in Column (h) from your Forms 8949
                  based on the box you checked. Then, ensure the totals match
                  the corresponding amounts on Schedule D.
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    If you{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      checked Box A or Box G,
                    </span>{' '}
                    the total should match the combined amounts of{' '}
                    <span style={{ fontWeight: 600 }}>
                      columns (d), (e), and (g) on Schedule D, Line 1b.
                    </span>
                  </li>
                  <li>
                    If you{' '}
                    <span style={{ fontWeight: 600 }}>
                      checked Box E or Box K,
                    </span>{' '}
                    the total should match the combined amounts of{' '}
                    <span style={{ fontWeight: 600 }}>
                      columns (d), (e), and (g) on Schedule D, Line 9.
                    </span>
                  </li>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#c026521f',
                    borderLeft: '5px solid #c02652',
                    borderRadius: 2,
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontStyle: 'italic', fontWeight: 600, mb: 0.8 }}
                  >
                    Example: Adjustment
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 0.8 }}>
                    If your sales price (Column d) is $6,000, your cost basis
                    (Column e) is $2,000, and you have a ($1,000) adjustment in
                    Column (g):
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    $6,000  ˆ’ $2,000 = $4,000
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    $4,000  ˆ’ $1,000 = $3,000 gain
                  </Typography>
                  <Typography variant="body1">
                    Enter $3,000 in Column (h).
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '20px !important',
                    fontWeight: 600,
                    mb: 0.8,
                    lineHeight: 1.5,
                  }}
                >
                  Part II - Long-Term
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Long-Term Capital Gains and Losses
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    A{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      long-term capital gain or loss
                    </span>{' '}
                    applies when you sell or dispose of a capital asset that you
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      held for more than one year
                    </span>
                    . Like short-term transactions, the holding period begins
                    the{' '}
                    <span style={{ fontWeight: 600 }}>
                      day after the asset is acquired
                    </span>{' '}
                    and includes the{' '}
                    <span style={{ fontWeight: 600 }}>day it is sold.</span>
                  </li>
                  <li>
                    These transactions are reported in{' '}
                    <span style={{ fontWeight: 600 }}>
                      Part II of Form 8949
                    </span>
                    . Long-term transactions often receive different tax
                    treatments, so separating them from short-term transactions
                    is an important step when preparing your tax return.
                  </li>
                  <li>
                    In certain situations, you may be able to report the
                    transaction directly on{' '}
                    <span style={{ fontWeight: 600 }}>
                      line 8a of Schedule D
                    </span>{' '}
                    instead of listing each transaction on Form 8949.
                  </li>
                  <li>
                    Another important rule involves{' '}
                    <span style={{ fontWeight: 600 }}>inherited property</span>.
                    When you sell property received through inheritance, the IRS
                    generally treats the transaction as{' '}
                    <span style={{ fontWeight: 600 }}>long-term,</span>{' '}
                    regardless of how long you actually held the asset before
                    selling it.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f8949-part-II-long-term-img.webp" />
                  <source srcSet="/assets/images/f8949-part-II-long-term-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8949-part-II-long-term-img.png"
                    loading="lazy"
                    alt="Form 8949  long‑term section showing Part II header for capital asset transactions held over one year, with selectable checkboxes D, E, F, J, K, and L for 1099‑B reporting."
                    width={760}
                    height={324}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 22, sm: 20, xs: 18 },
                    mb: 0.8,
                  }}
                >
                  Box Options for Long-Term Transactions
                </Typography>

                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Box D - Basis Reported to the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select <span style={{ fontWeight: 600 }}> Box D</span> if your
                  long-term transactions were reported on{' '}
                  <span style={{ fontWeight: 600 }}>Form 1099-B</span> and the
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    cost basis was also reported to the IRS
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Box E - Basis Not Reported to the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select <span style={{ fontWeight: 600 }}> Box E</span> if you
                  received <span style={{ fontWeight: 600 }}>Form 1099-B</span>,
                  but the{' '}
                  <span style={{ fontWeight: 600 }}>
                    cost basis was not reported to the IRS
                  </span>
                  . You will need to provide that information when completing
                  the form.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Box F - Transactions Not Reported on Form 1099
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select <span style={{ fontWeight: 600 }}>Box F</span> if your
                  long-term transactions (other than digital assets){' '}
                  <span style={{ fontWeight: 600 }}>
                    were not reported on Form 1099-B or Form 1099-DA.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Box J - Digital Asset Transactions with Basis Reported
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select <span style={{ fontWeight: 600 }}>Box J</span> if your
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    digital asset transactions
                  </span>{' '}
                  were reported on{' '}
                  <span style={{ fontWeight: 600 }}>Form 1099-DA</span> and the
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    cost basis was reported to the IRS
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Box K - Digital Asset Transactions with Basis Not Reported
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select <span style={{ fontWeight: 600 }}>Box K</span> if your
                  digital asset transactions were reported on{' '}
                  <span style={{ fontWeight: 600 }}>Form 1099-DA</span>, but the{' '}
                  <span style={{ fontWeight: 600 }}>
                    cost basis was not reported to the IRS
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Box L - Digital Asset Transactions Not Reported
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select <span style={{ fontWeight: 600 }}>Box L</span> if you
                  had{' '}
                  <span style={{ fontWeight: 600 }}>
                    long-term digital asset transactions that were not reported
                    to you on Form 1099-DA or Form 1099-B
                  </span>
                  .
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8949-part-II-table-img.webp" />
                  <source srcSet="/assets/images/f8949-part-II-table-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8949-part-II-table-img.png"
                    loading="lazy"
                    alt="Form 8949 Part II table for long-term transactions with columns for property description, dates, proceeds, cost basis, adjustments, and gain or loss."
                    width={760}
                    height={567}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 1 has already been explained in Part I above. If you need
                  additional clarification about how to complete this line or
                  understand the reporting requirements, please refer to{' '}
                  <Link href="#part1" sx={{ color: '#0202ff' }}>
                    {' '}
                    Part I of IRS Form 8949
                  </Link>
                  , where the instructions and examples are provided in detail.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    fontSize: { md: 20, xs: 18 },
                    mb: 0.8,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1">
                  Line 2 is used to{' '}
                  <span style={{ fontWeight: 600 }}>
                    calculate the total amounts for the transactions listed on
                    this page
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Column (d) Proceeds, Column (e) Cost or Other Basis, Column
                    (g) Adjustments, and Column (h) Gain or Loss
                  </span>
                  . While adding the values, make sure to subtract any negative
                  amounts.
                </Typography>
                <Typography variant="body1">
                  After calculating the totals,{' '}
                  <span style={{ fontWeight: 600 }}>
                    enter each column total on Line 2
                  </span>{' '}
                  and transfer the appropriate amount to{' '}
                  <span style={{ fontWeight: 600 }}>
                    Schedule D (Form 1040):
                  </span>
                </Typography>

                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}> Line 8b –</span> If{' '}
                    <span style={{ fontWeight: 600 }}>Box D or Box J</span> is
                    checked
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}> Line 9 –</span> If{' '}
                    <span style={{ fontWeight: 600 }}>Box E or Box K</span> is
                    checked
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}> Line 10 –</span> If{' '}
                    <span style={{ fontWeight: 600 }}>Box F or Box L</span> is
                    checked
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> If the
                  basis reported to the IRS is incorrect, enter the original
                  basis in Column (e) and use Column (g) to report an adjustment
                  to correct the amount.
                </Alert>

                <Alert
                  severity="info"
                  icon={false}
                  sx={{ borderRadius: 2, scrollMarginTop: 83 }}
                  id="add"
                >
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Additional IRS References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        Additional IRS References This content is based on
                        official{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i8949.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions
                        </Link>{' '}
                        for IRS Form 8949 issued by the Internal Revenue
                        Service. For complete and updated information, refer to
                        the official IRS website.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. How do I know if I need to file Form 8949?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    You need to file{' '}
                    <span style={{ fontWeight: 600 }}>Form 8949</span> if you
                    sold or exchanged any capital assets during the tax year and
                    received a{' '}
                    <Link
                      href="/information-returns/efile-irs-form-1099-b/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 1099-B
                    </Link>{' '}
                    or similar document reporting the transaction. This applies
                    to individuals, businesses, and tax-exempt organizations
                    with reportable sales or exchanges.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What is the difference between short-term and long-term
                  transactions on Form 8949?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                      Short-term transactions involve assets held for{' '}
                      <span style={{ fontWeight: 600 }}>one year or less</span>,
                      while long-term transactions involve assets held for more
                      than one year. These are reported separately on
                      <span style={{ fontWeight: 600 }}> Form 8949</span>{' '}
                      because they are subject to different tax rates.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What types of transactions are reported on Form 8949?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    <span style={{ fontWeight: 600 }}> Form 8949 </span> is used
                    to report the sale or exchange of{' '}
                    <span style={{ fontWeight: 600 }}>
                      capital assets, including stocks, bonds, real estate, and
                      other investments
                    </span>
                    . Each transaction’s details must be listed, including the
                    date of purchase, date of sale, proceeds, and cost basis.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form8949;
